/**
 * Quotes Page
 *
 * END_CONSUMER's view of received quotes for their cases.
 * Issue #152 - Frontend Entity CRUD & Workflow Operations
 * Issue #809 - Quote Lifecycle enhancements (i18n, detail drawer, accept/reject, compare)
 */

import MoneyIcon from '@mui/icons-material/AttachMoney';
import CompareIcon from '@mui/icons-material/CompareArrows';
import FilterIcon from '@mui/icons-material/FilterList';
import InfoIcon from '@mui/icons-material/Info';
import SearchIcon from '@mui/icons-material/Search';
import ViewIcon from '@mui/icons-material/Visibility';
import {
  Box,
  DataTable,
  type DataTableColumn,
  GlassAlert,
  GlassButton,
  GlassCard,
  GlassCheckbox,
  GlassChip,
  GlassIconButton,
  GlassTooltip,
  Grid,
  InputAdornment,
  MagicInput,
  ShimmerLoader,
  SplitPanel,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import { type MouseEvent, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageTemplate } from '../../components/layout/PageTemplate';
import { useConsumerQuotes } from '../../lib/trpc/hooks';
import QuoteComparisonView from './QuoteComparisonView';
import QuoteDetailContent from './QuoteDetailContent';

interface Quote {
  id: string;
  caseId: string;
  caseName: string;
  lenderName: string;
  amount: number;
  rate: number;
  term: number;
  monthlyPayment: number;
  status: string;
  receivedAt: string;
  expiresAt: string;
  calculations?: Record<string, unknown>;
}

const PENDING_QUOTE_STATUSES: ReadonlySet<string> = new Set(['pending', 'active', 'sent']);

const STATUS_COLORS: Record<string, 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info'> = {
  draft: 'default',
  active: 'info',
  pending: 'info',
  sent: 'primary',
  viewed: 'primary',
  locked: 'warning',
  accepted: 'success',
  rejected: 'error',
  declined: 'error',
  expired: 'warning',
  counter_offer: 'info',
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/** Maximum number of quotes that can be compared simultaneously */
const MAX_COMPARE = 3;

export default function QuotesPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState<Set<string>>(new Set());
  const [showComparison, setShowComparison] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Fetch real quotes from pricing service (Issue #186)
  const { data: quotesResponse, isLoading, isError, error } = useConsumerQuotes({ page, limit: pageSize });

  // Quotes from API response (real data from pricing service)
  const quotes: Quote[] = useMemo(() => {
    if (!quotesResponse?.data) return [];
    return quotesResponse.data as Quote[];
  }, [quotesResponse]);

  // Filter quotes by search query
  const filteredQuotes = useMemo(() => {
    if (!searchQuery) return quotes;
    const query = searchQuery.toLowerCase();
    return quotes.filter((q) => q.caseName.toLowerCase().includes(query) || q.lenderName.toLowerCase().includes(query));
  }, [quotes, searchQuery]);

  // Calculate stats
  const stats = useMemo(
    () => ({
      totalQuotes: filteredQuotes.length,
      pending: filteredQuotes.filter((q) => PENDING_QUOTE_STATUSES.has(q.status)).length,
      accepted: filteredQuotes.filter((q) => q.status === 'accepted').length,
      bestRate: filteredQuotes.length > 0 ? Math.min(...filteredQuotes.map((q) => q.rate)) : 0,
    }),
    [filteredQuotes],
  );

  const getStatusLabel = useCallback(
    (status: string): string => {
      const key = `quotes.status.${status}` as const;
      const translated = t(key);
      return translated !== key ? translated : status.charAt(0).toUpperCase() + status.slice(1);
    },
    [t],
  );

  const handleToggleCompareMode = () => {
    if (compareMode) {
      // Exiting compare mode
      setCompareMode(false);
      setSelectedForCompare(new Set());
      setShowComparison(false);
    } else {
      setCompareMode(true);
    }
  };

  const handleToggleQuoteSelection = useCallback((quoteId: string, event: MouseEvent) => {
    event.stopPropagation();
    setSelectedForCompare((prev) => {
      const next = new Set(prev);
      if (next.has(quoteId)) {
        next.delete(quoteId);
      } else if (next.size < MAX_COMPARE) {
        next.add(quoteId);
      }
      return next;
    });
  }, []);

  const handleCompareSelected = () => {
    if (selectedForCompare.size >= 2) {
      setShowComparison(true);
    }
  };

  const handleCloseComparison = () => {
    setShowComparison(false);
  };

  const handleSelectQuoteFromComparison = (quoteId: string) => {
    setSelectedQuoteId(quoteId);
  };

  // Get the selected quotes for comparison
  const comparisonQuotes = useMemo(() => {
    return filteredQuotes.filter((q) => selectedForCompare.has(q.id));
  }, [filteredQuotes, selectedForCompare]);

  const columns: DataTableColumn<Quote>[] = useMemo(() => {
    const cols: DataTableColumn<Quote>[] = [];

    // Checkbox column when in compare mode
    if (compareMode) {
      cols.push({
        id: 'compare',
        header: '',
        accessor: 'id',
        cell: (_value, row) => {
          const isChecked = selectedForCompare.has(row.id);
          const isDisabled = !isChecked && selectedForCompare.size >= MAX_COMPARE;
          return (
            <GlassCheckbox
              checked={isChecked}
              disabled={isDisabled}
              onClick={(e) => handleToggleQuoteSelection(row.id, e)}
              size="small"
              sx={{ p: 0 }}
            />
          );
        },
      });
    }

    cols.push(
      {
        id: 'caseName',
        header: t('quotes.column.case'),
        accessor: 'caseName',
        sortable: true,
      },
      {
        id: 'lenderName',
        header: t('quotes.column.lender'),
        accessor: 'lenderName',
        sortable: true,
      },
      {
        id: 'amount',
        header: t('quotes.column.amount'),
        accessor: 'amount',
        sortable: true,
        cell: (value) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatCurrency(value as number)}
          </Typography>
        ),
      },
      {
        id: 'rate',
        header: t('quotes.column.rate'),
        accessor: 'rate',
        sortable: true,
        cell: (value) => `${value}%`,
      },
      {
        id: 'term',
        header: t('quotes.column.term'),
        accessor: 'term',
        sortable: true,
        cell: (value) => {
          const termValue = value as number;
          return termValue >= 12
            ? t('quotes.termYears', { count: Math.round(termValue / 12) })
            : t('quotes.termMonths', { count: termValue });
        },
      },
      {
        id: 'status',
        header: t('quotes.column.status'),
        accessor: 'status',
        sortable: true,
        cell: (_value, row) => (
          <GlassChip label={getStatusLabel(row.status)} size="small" color={STATUS_COLORS[row.status] || 'default'} />
        ),
      },
      {
        id: 'receivedAt',
        header: t('quotes.column.received'),
        accessor: 'receivedAt',
        sortable: true,
        cell: (value) => new Date(value as string).toLocaleDateString(),
      },
      {
        id: 'actions',
        header: '',
        accessor: 'id',
        cell: (_value, row) => (
          <GlassTooltip title={t('quotes.viewCase')}>
            <GlassIconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/applications/${row.caseId}`);
              }}
            >
              <ViewIcon fontSize="small" />
            </GlassIconButton>
          </GlassTooltip>
        ),
      },
    );

    return cols;
  }, [compareMode, selectedForCompare, t, navigate, getStatusLabel, handleToggleQuoteSelection]);

  const panelContent = selectedQuoteId ? (
    <QuoteDetailContent quoteId={selectedQuoteId} onClose={() => setSelectedQuoteId(null)} />
  ) : null;

  return (
    <PageTemplate
      variant="list"
      titleKey="consumerPortal.myQuotes"
      descriptionKey="consumerPortal.myQuotesDescription"
      icon={MoneyIcon}
    >
      <SplitPanel
        panelOpen={selectedQuoteId !== null}
        onPanelClose={() => setSelectedQuoteId(null)}
        panel={panelContent}
        header={
          <>
            {/* Stats */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <GlassCard neonAccent="cyan" animate>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <MoneyIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body2" color="textSecondary">
                      {t('quotes.totalQuotes')}
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {stats.totalQuotes}
                  </Typography>
                </GlassCard>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <GlassCard neonAccent="amber" animate animationDelay={0.1}>
                  <Typography variant="body2" color="textSecondary">
                    {t('quotes.pendingReview')}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'info.main' }}>
                    {stats.pending}
                  </Typography>
                </GlassCard>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <GlassCard neonAccent="green" animate animationDelay={0.2}>
                  <Typography variant="body2" color="textSecondary">
                    {t('quotes.accepted')}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {stats.accepted}
                  </Typography>
                </GlassCard>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <GlassCard neonAccent="cyan" animate animationDelay={0.3}>
                  <Typography variant="body2" color="textSecondary">
                    {t('quotes.bestRate')}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {stats.bestRate > 0 ? `${stats.bestRate}%` : '-'}
                  </Typography>
                </GlassCard>
              </Grid>
            </Grid>

            {/* Info Alert */}
            <GlassAlert severity="info" visible={true} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon fontSize="small" />
                <Typography variant="body2">{t('quotes.infoBanner')}</Typography>
              </Box>
            </GlassAlert>

            {/* Comparison View (shown when comparing) */}
            {showComparison && comparisonQuotes.length >= 2 && (
              <Box sx={{ mb: 3 }}>
                <QuoteComparisonView
                  quotes={comparisonQuotes}
                  onClose={handleCloseComparison}
                  onSelectQuote={handleSelectQuoteFromComparison}
                />
              </Box>
            )}
          </>
        }
      >
        <GlassCard animate animationDelay={0.4}>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
            <MagicInput
              size="small"
              placeholder={t('quotes.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ width: 300 }}
            />
            <GlassButton variant="outlined" startIcon={<FilterIcon />}>
              {t('quotes.filters')}
            </GlassButton>

            {/* Compare toggle */}
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
              {compareMode && selectedForCompare.size > 0 && (
                <Typography variant="body2" color="textSecondary">
                  {t('quotes.compare.selected', { count: selectedForCompare.size })}
                </Typography>
              )}
              {compareMode && selectedForCompare.size >= 2 && (
                <GlassButton
                  variant="glow"
                  color="primary"
                  startIcon={<CompareIcon />}
                  onClick={handleCompareSelected}
                  size="small"
                >
                  {t('quotes.compare.compare')}
                </GlassButton>
              )}
              <GlassButton
                variant={compareMode ? 'contained' : 'outlined'}
                color={compareMode ? 'primary' : 'inherit'}
                startIcon={<CompareIcon />}
                onClick={handleToggleCompareMode}
                size="small"
              >
                {compareMode ? t('quotes.compare.exitCompare') : t('quotes.compare.title')}
              </GlassButton>
            </Box>
          </Box>

          {/* Compare mode hint */}
          {compareMode && selectedForCompare.size < 2 && (
            <GlassAlert severity="info" visible={true} sx={{ mb: 2 }}>
              {t('quotes.compare.selectTwo')}
            </GlassAlert>
          )}

          {isError ? (
            <GlassAlert severity="error" visible={true} sx={{ mb: 2 }}>
              {error?.message || t('error.unknown')}
            </GlassAlert>
          ) : isLoading ? (
            <Box>
              {[1, 2, 3, 4, 5].map((i) => (
                <Box key={i} sx={{ mb: 1 }}>
                  <ShimmerLoader variant="rectangular" height={48} />
                </Box>
              ))}
            </Box>
          ) : filteredQuotes.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="textSecondary" sx={{ mb: 2 }}>
                {searchQuery ? t('quotes.noQuotes') : `${t('quotes.empty')}. ${t('quotes.emptyDescription')}`}
              </Typography>
              {!searchQuery && (
                <GlassButton variant="outlined" onClick={() => navigate('/applications/new')}>
                  {t('quotes.submitNewCase')}
                </GlassButton>
              )}
            </Box>
          ) : (
            <DataTable
              columns={columns}
              data={filteredQuotes}
              getRowId={(row) => row.id}
              onRowClick={(row) => {
                if (!compareMode) {
                  setSelectedQuoteId(row.id);
                }
              }}
              pagination={{
                page: page - 1,
                pageSize,
                totalRows: quotesResponse?.total ?? 0,
              }}
              onPaginationChange={(config) => {
                setPage(config.page + 1);
                setPageSize(config.pageSize);
              }}
            />
          )}
        </GlassCard>
      </SplitPanel>
    </PageTemplate>
  );
}
